import { FunctionComponent, MouseEvent, useLayoutEffect, useRef, useState } from "react";

import { format } from "date-fns";
import { Link } from "react-router-dom";

import { faExternalLink } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";

import { LinkedinSignalPostPreview } from "./LinkedinSignalPostPreview";
import { SignalPreview, getLinkedinPostDate, getLinkedinPostUrl } from "./signalTypes";
import {
	getRelativeTimeString,
	getSignalCategoryColor,
	getSignalPriorityColor,
	getSignalPriorityLabel,
} from "./signalTimelineUtils";

interface SignalResultContentProps {
	signal: SignalPreview;
	isSummaryExpanded: boolean;
	isReasoningExpanded?: boolean;
	onToggleSummary: (signalId: number) => void;
	onToggleReasoning?: (signalId: number) => void;
	stopPropagationOnActions?: boolean;
	hidePausedError?: boolean;
}

const formatDateTime = (date: string | Date): string => format(new Date(date), "d MMM yyyy, HH:mm");

export const SignalResultContent: FunctionComponent<SignalResultContentProps> = ({
	signal,
	isSummaryExpanded,
	isReasoningExpanded = true,
	onToggleSummary,
	onToggleReasoning,
	stopPropagationOnActions = false,
	hidePausedError = false,
}) => {
	const sourceUrl = getLinkedinPostUrl(signal);
	const signalDate = getLinkedinPostDate(signal) ?? signal.createdAt;
	const hasLinkedinPostPreview = Boolean(signal.linkedinPost);
	const shouldShowError =
		signal.status === "ERROR" &&
		signal.errorMessage &&
		!(hidePausedError && signal.errorMessage === "PAUSED");

	const handleActionClick = (event: MouseEvent<HTMLElement>) => {
		if (stopPropagationOnActions) {
			event.stopPropagation();
		}
	};

	// Only show the "Read more/less" toggles when the text actually overflows the
	// 2-line clamp. Measure while collapsed (the clamped state); when expanded we
	// keep the previous result so the "Read less" affordance stays available.
	const summaryRef = useRef<HTMLParagraphElement>(null);
	const reasoningRef = useRef<HTMLParagraphElement>(null);
	const [isSummaryClamped, setIsSummaryClamped] = useState(false);
	const [isReasoningClamped, setIsReasoningClamped] = useState(false);

	useLayoutEffect(() => {
		const measure = () => {
			const summaryEl = summaryRef.current;

			if (summaryEl && !isSummaryExpanded) {
				setIsSummaryClamped(summaryEl.scrollHeight > summaryEl.clientHeight + 1);
			}

			const reasoningEl = reasoningRef.current;

			if (reasoningEl && !isReasoningExpanded) {
				setIsReasoningClamped(reasoningEl.scrollHeight > reasoningEl.clientHeight + 1);
			}
		};

		measure();
		window.addEventListener("resize", measure);

		return () => window.removeEventListener("resize", measure);
	}, [signal.summary, signal.reasoning, isSummaryExpanded, isReasoningExpanded]);

	return (
		<>
			<div className="mb-2 flex flex-row flex-wrap justify-between gap-2">
				<div className="flex flex-wrap items-center gap-2">
					{signal.priority && (
						<span
							className={classNames(
								"rounded border px-2 py-0.5 text-xs font-semibold",
								getSignalPriorityColor(signal.priority)
							)}
						>
							{getSignalPriorityLabel(signal.priority)}
						</span>
					)}
					{signal.signalCategory && (
						<span
							className={classNames(
								"rounded border px-2 py-0.5 text-xs font-semibold",
								getSignalCategoryColor(signal.signalCategory)
							)}
						>
							{signal.signalCategory}
						</span>
					)}
					{sourceUrl && (
						<Link
							className="flex items-center"
							target="_blank"
							to={sourceUrl}
							onClick={handleActionClick}
						>
							{sourceUrl.includes("linkedin.com") ? (
								<LinkedinIcon />
							) : (
								<Icon
									className="h-4 w-4 text-text-meta hover:text-blue-action"
									icon={faExternalLink}
								/>
							)}
						</Link>
					)}
				</div>

				<div className="flex flex-col items-end text-xs text-text-meta">
					<Tooltip content={<SignalTimeDetails signal={signal} />}>
						<span className="cursor-help">{getRelativeTimeString(signalDate)}</span>
					</Tooltip>
				</div>
			</div>

			{signal.title ? (
				<h4 className="mb-2 text-sm font-normal leading-5 text-text">{signal.title}</h4>
			) : (
				<h4 className="mb-2 text-sm font-semibold italic text-disabled">No title</h4>
			)}

			{hasLinkedinPostPreview && <LinkedinSignalPostPreview signal={signal} />}

			{signal.reasoning && (
				<div className="mb-3 rounded-lg border-l-2 border-blue-action bg-action-200 p-3">
					<h5 className="mb-1 text-xs font-semibold text-text-meta">Why is this relevant</h5>
					<p
						ref={reasoningRef}
						className={classNames("text-xs text-text-meta", !isReasoningExpanded && "line-clamp-2")}
					>
						{signal.reasoning}
					</p>
					{onToggleReasoning && (isReasoningClamped || isReasoningExpanded) && (
						<button
							className="mt-0 cursor-pointer text-xs text-blue-action hover:underline"
							onClick={(event) => {
								handleActionClick(event);
								onToggleReasoning(signal.id);
							}}
						>
							{isReasoningExpanded ? "Read less" : "Read more"}
						</button>
					)}
				</div>
			)}

			{signal.summary ? (
				<div className="mb-3">
					<p
						ref={summaryRef}
						className={classNames("text-sm text-text-meta", !isSummaryExpanded && "line-clamp-2")}
					>
						{signal.summary}
					</p>
					{(isSummaryClamped || isSummaryExpanded) && (
						<button
							className="mt-0 cursor-pointer text-xs text-text-meta hover:underline"
							onClick={(event) => {
								handleActionClick(event);
								onToggleSummary(signal.id);
							}}
						>
							{isSummaryExpanded ? "Read less" : "Read more"}
						</button>
					)}
				</div>
			) : (
				<p className="mb-3 text-sm italic text-text-meta">No description</p>
			)}

			{shouldShowError && (
				<div className="mt-2 rounded border border-error bg-error-light p-2 text-xs text-error">
					Error: {signal.errorMessage}
				</div>
			)}
		</>
	);
};

const SignalTimeDetails = ({ signal }: { signal: SignalPreview }) => (
	<div className="flex flex-col gap-1">
		{getLinkedinPostDate(signal) && (
			<span>Published: {formatDateTime(getLinkedinPostDate(signal)!)}</span>
		)}
		<span>Collected: {formatDateTime(signal.createdAt)}</span>
	</div>
);
